import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    Banknote,
    Search,
    Eye,
    Edit,
    CheckCircle,
    XCircle,
    Clock,
    DollarSign,
    Calendar,
    Building,
    Plus,
    Loader2,
    AlertCircle,
    CreditCard,
    Receipt,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { payoutService } from '@/services/payoutService'
import { MerchantSettlement } from '@/types'
import { safeFormatDistanceToNow } from '@/utils/date'

export default function AdminSettlementsList() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const [settlements, setSettlements] = useState<MerchantSettlement[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('')
    const [methodFilter, setMethodFilter] = useState<string>('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [pageSize] = useState(20)

    useEffect(() => {
        fetchSettlements()
    }, [currentPage, statusFilter, methodFilter])

    const fetchSettlements = async () => {
        try {
            setLoading(true)
            setError(null)

            const params = {
                page: currentPage,
                page_size: pageSize,
                ...(statusFilter && { status: statusFilter }),
                ...(methodFilter && { method: methodFilter }),
            }

            const response = await payoutService.getSettlements(params)

            if (response.success) {
                setSettlements(response.data.results)
                setTotalCount(response.data.count)
            } else {
                setError('Failed to fetch settlements')
            }
        } catch (err) {
            setError('An error occurred while fetching settlements')
            console.error('Error fetching settlements:', err)
        } finally {
            setLoading(false)
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'initiated':
                return 'text-blue-600 bg-blue-100 border-blue-200'
            case 'pending':
                return 'text-yellow-600 bg-yellow-100 border-yellow-200'
            case 'completed':
                return 'text-green-600 bg-green-100 border-green-200'
            case 'failed':
                return 'text-red-600 bg-red-100 border-red-200'
            default:
                return 'text-gray-600 bg-gray-100 border-gray-200'
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'initiated':
                return Clock
            case 'pending':
                return Clock
            case 'completed':
                return CheckCircle
            case 'failed':
                return XCircle
            default:
                return Clock
        }
    }

    const getMethodIcon = (method: string) => {
        switch (method) {
            case 'bank_transfer':
                return Building
            case 'crypto':
                return CreditCard
            case 'manual':
                return Banknote
            default:
                return Banknote
        }
    }

    const handleSettlementAction = async (settlementId: number, action: string) => {
        try {
            let response
            switch (action) {
                case 'mark_completed':
                    response = await payoutService.markSettlementCompleted(settlementId)
                    break
                case 'mark_failed':
                    response = await payoutService.markSettlementFailed(settlementId)
                    break
            }

            if (response?.success) {
                // Refresh the list
                await fetchSettlements()
            }
        } catch (err) {
            console.error(`Error performing ${action} on settlement:`, err)
        }
    }

    const filteredSettlements = settlements.filter(settlement =>
        settlement.merchant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        settlement.id.toString().includes(searchTerm) ||
        (settlement.reference && settlement.reference.toLowerCase().includes(searchTerm.toLowerCase()))
    )

    const totalPages = Math.ceil(totalCount / pageSize)

    if (loading && settlements.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: branding.primaryColor }} />
                    <p style={pageStyles.textSecondary}>Loading settlements...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen" style={pageStyles.container}>
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <Button
                            onClick={() => navigate('/admin/settlements/create')}
                            className="flex items-center text-white"
                            style={pageStyles.primaryBg}
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Settlement
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="border backdrop-blur-md shadow-sm p-6 mb-6" style={cardStyles}>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>Search</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: branding.textSecondaryColor }} />
                                <Input
                                    placeholder="Search by merchant, ID, or reference..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 border"
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}30`,
                                        color: branding.textColor
                                    }}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                style={{
                                    backgroundColor: branding.surfaceColor,
                                    borderColor: `${branding.textSecondaryColor}30`,
                                    color: branding.textColor,
                                }}
                            >
                                <option value="">All Statuses</option>
                                <option value="initiated">Initiated</option>
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                                <option value="failed">Failed</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>Method</label>
                            <select
                                value={methodFilter}
                                onChange={(e) => setMethodFilter(e.target.value)}
                                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                style={{
                                    backgroundColor: branding.surfaceColor,
                                    borderColor: `${branding.textSecondaryColor}30`,
                                    color: branding.textColor,
                                }}
                            >
                                <option value="">All Methods</option>
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="crypto">Cryptocurrency</option>
                                <option value="manual">Manual</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <Button
                                onClick={() => {
                                    setStatusFilter('')
                                    setMethodFilter('')
                                    setSearchTerm('')
                                }}
                                variant="outline"
                                className="w-full"
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="rounded-lg p-4 mb-6" style={{
                        backgroundColor: statusColors.error.backgroundColor,
                        borderColor: statusColors.error.borderColor,
                        border: '1px solid'
                    }}>
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 mr-2" style={{ color: statusColors.error.color }} />
                            <p style={{ color: statusColors.error.color }}>{error}</p>
                        </div>
                    </div>
                )}

                {/* Settlements Table */}
                <div className="border backdrop-blur-md shadow-sm overflow-hidden" style={cardStyles}>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y" style={{ borderColor: `${branding.textSecondaryColor}30` }}>
                            <thead style={{ backgroundColor: `${branding.backgroundColor}80` }}>
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Settlement ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Merchant
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Method
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Reference
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y" style={{
                                backgroundColor: `${branding.surfaceColor}30`,
                                borderColor: `${branding.textSecondaryColor}30`
                            }}>
                                {filteredSettlements.map((settlement) => {
                                    const StatusIcon = getStatusIcon(settlement.status)
                                    const MethodIcon = getMethodIcon(settlement.method)
                                    return (
                                        <tr key={settlement.id} className="hover:bg-white/5" style={{ backgroundColor: `${branding.primaryColor}05` }}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={pageStyles.text}>
                                                #{settlement.id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-2" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                                        <Building className="h-4 w-4" style={{ color: branding.primaryColor }} />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium" style={pageStyles.text}>{settlement.merchant_name}</div>
                                                        {settlement.submerchant_name && (
                                                            <div className="text-sm" style={pageStyles.textSecondary}>{settlement.submerchant_name}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.text}>
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-1" style={{ backgroundColor: `${statusColors.success.backgroundColor}` }}>
                                                        <DollarSign className="h-4 w-4" style={{ color: statusColors.success.color }} />
                                                    </div>
                                                    ${Number(settlement.amount_fiat).toFixed(2)} {settlement.currency}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-2" style={{ backgroundColor: `${branding.secondaryColor}20` }}>
                                                        <MethodIcon className="h-4 w-4" style={{ color: branding.secondaryColor }} />
                                                    </div>
                                                    <span className="text-sm" style={pageStyles.text}>
                                                        {settlement.method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.textSecondary}>
                                                {settlement.reference ? (
                                                    <span className="font-mono text-xs px-2 py-1 rounded" style={{ backgroundColor: `${branding.textSecondaryColor}20` }}>
                                                        {settlement.reference}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: `${branding.textSecondaryColor}80` }}>No reference</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(settlement.status)}`}>
                                                    <StatusIcon className="h-3 w-3 mr-1" />
                                                    {settlement.status.charAt(0).toUpperCase() + settlement.status.slice(1)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.textSecondary}>
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-1" style={{ backgroundColor: `${statusColors.info.backgroundColor}` }}>
                                                        <Calendar className="h-4 w-4" style={{ color: statusColors.info.color }} />
                                                    </div>
                                                    {safeFormatDistanceToNow(settlement.created_at, 'Unknown', { addSuffix: true })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/settlement-items/${settlement.id}`)}
                                                        style={{ color: branding.textSecondaryColor }}
                                                        className="hover:text-white hover:bg-gray-700"
                                                        title="View Settlement Items"
                                                    >
                                                        <Receipt className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/settlements/${settlement.id}`)}
                                                        style={{ color: branding.textSecondaryColor }}
                                                        className="hover:text-white hover:bg-gray-700"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/settlements/${settlement.id}/edit`)}
                                                        style={{ color: branding.textSecondaryColor }}
                                                        className="hover:text-white hover:bg-gray-700"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    {settlement.status === 'pending' && (
                                                        <>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleSettlementAction(settlement.id, 'mark_completed')}
                                                                style={{ color: statusColors.success.color }}
                                                                className="hover:text-green-700"
                                                            >
                                                                <CheckCircle className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleSettlementAction(settlement.id, 'mark_failed')}
                                                                style={{ color: statusColors.error.color }}
                                                                className="hover:text-red-700"
                                                            >
                                                                <XCircle className="h-4 w-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {filteredSettlements.length === 0 && !loading && (
                        <div className="text-center py-12">
                            <Banknote className="h-12 w-12 mx-auto mb-4" style={{ color: branding.textSecondaryColor }} />
                            <h3 className="text-lg font-medium mb-2" style={pageStyles.text}>No settlements found</h3>
                            <p className="mb-4" style={pageStyles.textSecondary}>
                                {searchTerm || statusFilter || methodFilter
                                    ? 'Try adjusting your filters to see more results.'
                                    : 'Get started by creating your first settlement.'}
                            </p>
                            <Button onClick={() => navigate('/admin/settlements/create')} className="text-white" style={pageStyles.primaryBg}>
                                <Plus className="h-4 w-4 mr-2" />
                                Create Settlement
                            </Button>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6">
                        <div className="text-sm" style={pageStyles.textSecondary}>
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                Previous
                            </Button>
                            <span className="text-sm" style={pageStyles.textSecondary}>
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
